import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Button, Field } from '../components/ui';
import { t } from '../i18n';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { AuthShell } from './LoginPage';

const MIN_LENGTH = 12;

/** Validación local de la contraseña nueva (la API vuelve a validar). Devuelve el mensaje de error o null. */
export function newPasswordProblem(password: string, confirm: string): string | null {
  if (password.length < MIN_LENGTH) return t('clave.muy_corta');
  if (password !== confirm) return t('clave.no_coinciden');
  return null;
}

/** "Olvidé mi contraseña": la respuesta es siempre la misma, exista o no la cuenta. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/clave/olvido', { json: { email: email.trim() } });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('clave.recuperar_titulo')}>
      {sent ? (
        <div className="space-y-4">
          <Alert tone="success">{t('clave.recuperar_enviado')}</Alert>
          <Link to="/login" className="text-sm text-marca hover:underline">
            {t('clave.ir_al_login')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-slate-600">{t('clave.recuperar_instruccion')}</p>
          {error && <Alert>{error}</Alert>}
          <Field id="correo" label={t('login.correo')} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" className="w-full" disabled={busy || !email.includes('@')}>
            {busy ? t('comun.cargando') : t('clave.recuperar_enviar')}
          </Button>
          <Link to="/login" className="block text-center text-sm text-marca hover:underline">
            {t('comun.volver')}
          </Link>
        </form>
      )}
    </AuthShell>
  );
}

/**
 * Página del enlace del correo: /restablecer-clave#token=…
 * El token va en el fragmento (#): el navegador no lo envía al servidor ni a otros sitios. Se lee una vez y se
 * quita de la barra de direcciones para que no quede en el historial.
 */
export function ResetPasswordPage() {
  const [token] = useState(() => {
    const value = new URLSearchParams(window.location.hash.slice(1)).get('token');
    if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname);
    return value;
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = newPasswordProblem(password, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/clave/restablecer', { json: { token, newPassword: password } });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('clave.restablecer_titulo')}>
      {!token ? (
        <div className="space-y-4">
          <Alert>{t('clave.enlace_invalido')}</Alert>
          <Link to="/recuperar-clave" className="text-sm text-marca hover:underline">
            {t('clave.recuperar_titulo')}
          </Link>
        </div>
      ) : done ? (
        <div className="space-y-4">
          <Alert tone="success">{t('clave.restablecida')}</Alert>
          <Link to="/login" className="text-sm text-marca hover:underline">
            {t('clave.ir_al_login')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-xs text-slate-500">{t('clave.politica')}</p>
          {error && <Alert>{error}</Alert>}
          <Field id="nueva" label={t('clave.nueva')} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Field id="confirmar" label={t('clave.confirmar')} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button type="submit" className="w-full" disabled={busy || !password || !confirm}>
            {busy ? t('comun.cargando') : t('clave.guardar')}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

/** Cambio de contraseña con la sesión iniciada (tarjeta de Mi seguridad). */
export function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = newPasswordProblem(password, confirm);
    if (problem) {
      setMessage({ tone: 'error', text: problem });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api('/api/auth/clave/cambiar', { json: { currentPassword: current, newPassword: password } });
      setMessage({ tone: 'success', text: t('clave.cambiada') });
      setCurrent('');
      setPassword('');
      setConfirm('');
    } catch (err) {
      setMessage({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3" noValidate>
      <p className="text-xs text-slate-500">{t('clave.politica')}</p>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <Field id="clave-actual" label={t('clave.actual')} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      <Field id="clave-nueva" label={t('clave.nueva')} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      <Field id="clave-confirmar" label={t('clave.confirmar')} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      <Button type="submit" disabled={busy || !current || !password || !confirm}>
        {t('clave.cambiar')}
      </Button>
    </form>
  );
}
